import './textEditor.scss';

import CheckIcon from '@mui/icons-material/Check';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/system';
import { Box } from '@mui/system';
import type { AnyExtension } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskList from '@tiptap/extension-task-list';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { UserInfoWithIdT } from 'business/definitions';
import type { ModuleT } from 'business/types';
import { useFieldMentionExtension } from 'hooks/FieldMention/useFieldMentionExtension';
import { useGetSuggestedUsers } from 'hooks/useFetchSuggestedUsers';
import { isArgumentFromLocation } from 'modules/budget/components/BudgetProposal/Resource/Arguments/Argument/helpers';
import { FractionExtension } from 'modules/datacatalog/components/FormulaEditor/FractionExtension';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SHADOW } from 'styles/theme';
import Mention from 'ui/UserMention/mantyMention';
import { MentionStyle } from 'ui/UserMention/styles';
import { Suggestion } from 'ui/UserMention/Suggestion';

import EnrichedLinkExtension from './EnrichedLinkExtension';
import { EnrichedTaskItemStyleExtension } from './EnrichedTaskItemStyleExtension';
import { EnrichedTextStyleExtension } from './EnrichedTextStyleExtension';
import { useAutoSave } from './hooks';
import MenuBar from './MenuBar';

const StyEditorContent = styled(EditorContent, {
    name: 'StyEditorContent',
    shouldForwardProp: (prop) => prop !== 'isArgument',
})<{ isArgument: boolean }>(({ isArgument, theme }) => ({
    '& .ProseMirror': {
        padding: '0 0.5rem',
    },
    '& p': {
        fontSize: isArgument ? '16px' : '14px',
    },
    ...MentionStyle(theme),
}));

const StyPopper = styled(Popper, {
    name: 'StyPopper',
    shouldForwardProp: (prop) => prop !== 'width',
})<{
    width: number;
}>(({ theme, width }) => ({
    width,
    zIndex: '2 !important',
    boxShadow: SHADOW,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
}));

const StySaveIndicator = styled('div', {
    name: 'StySaveIndicator',
})(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.success.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    zIndex: 10,
    pointerEvents: 'none',
    transition: 'opacity 0.3s ease-in-out',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(0.25, 0.75),
    borderRadius: theme.spacing(0.5),
}));

type Props = {
    isEditable: boolean;
    content: string;
    canEdit: boolean;
    parentRef?: RefObject<HTMLDivElement> | null;
    sx?: SxProps<Theme>;
    onSave: (
        html: string,
        users?: UserInfoWithIdT[],
        options?: { isAutoSave?: boolean },
    ) => void;
    module: Extract<ModuleT, 'decision' | 'budget'>;
    isParagraphWidget?: boolean;
    mentionBoundingBox?: { left: number; right: number };
    trackerFunctions?: Partial<{
        trackMentionCharClicked?: () => void;
        trackMentionCreated?: () => void;
    }>;
    autoSaveDelay?: number;
    additionalExtensions?: AnyExtension[];
    mentionType?: 'user' | 'field' | 'none';
    deactivateAutoSave?: boolean;
    emptyTextPlaceholder?: string;
};

export default function TextEditor({
    isEditable,
    content,
    canEdit,
    onSave,
    sx = {},
    parentRef = undefined,
    module,
    isParagraphWidget = false,
    mentionBoundingBox = undefined,
    trackerFunctions = undefined,
    autoSaveDelay = 1000,
    additionalExtensions = [],
    mentionType = 'user',
    deactivateAutoSave = false,
    emptyTextPlaceholder = undefined,
}: Props) {
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const {
        data: users,
        isFetching,
        isLoading,
    } = useGetSuggestedUsers(module, mentionType === 'user');

    const fieldMentionExtension = useFieldMentionExtension(
        mentionType === 'field',
    );

    const { savingStatus, cancelAutoSave, debouncedSave } = useAutoSave({
        onSave,
        users,
        autoSaveDelay,
        content,
    });

    const mentionExtension =
        mentionType === 'none'
            ? []
            : mentionType === 'user'
              ? [
                    Mention.configure({
                        boundingBox: mentionBoundingBox ?? {
                            left: 56,
                            right: 56,
                        },
                        margin: 16,
                        isParagraphWidget,
                        suggestion: Suggestion(
                            users,
                            trackerFunctions?.trackMentionCreated,
                        ),
                    }),
                ]
              : [fieldMentionExtension];

    const onSaveAction = users
        ? (html: string, options?: { isAutoSave?: boolean }) =>
              onSave(html, users, options)
        : onSave;

    const editor = useEditor(
        {
            editable: isEditable,
            extensions: [
                StarterKit.configure({
                    heading: {
                        levels: [1, 2],
                    },
                }),
                Highlight.configure({ multicolor: true }),
                Color,
                EnrichedTextStyleExtension,
                Underline,
                Image.configure({ allowBase64: true }),
                EnrichedLinkExtension,
                Placeholder.configure({
                    placeholder: (tiptapProps) => {
                        if (!canEdit) {
                            return '';
                        }
                        if (emptyTextPlaceholder) return emptyTextPlaceholder;
                        if (tiptapProps.editor.options.editable)
                            return t(
                                'dashboardHeader.editingEmptyParagraphWithMention',
                            );
                        return t('dashboardHeader.emptyParagraph');
                    },
                    showOnlyWhenEditable: false,
                }),
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
                Table.configure({
                    resizable: true,
                }),
                TableRow,
                TableHeader,
                TableCell,
                TaskList,
                EnrichedTaskItemStyleExtension.configure({
                    // NB: "onReadOnlyChecked: () => canEdit" would be a better UX, because
                    // it would allow the (un)checking of boxes without going into edit mode,
                    // but as of version 2.0beta209, the 'update' event is not triggered when
                    // a checkbox is edit and isEditable is false
                    // see https://github.com/ueberdosis/tiptap/issues/3676
                    onReadOnlyChecked: () => false,
                    nested: true,
                }),
                ...mentionExtension,
                ...(mentionType === 'field' || mentionType === 'none'
                    ? [FractionExtension]
                    : []),
                ...additionalExtensions,
            ],
            content,
            onUpdate: ({ editor: updatedEditor }) => {
                if (!deactivateAutoSave) {
                    debouncedSave(updatedEditor.getHTML());
                }
            },
        },
        [isFetching, isLoading],
    );

    useEffect(() => {
        if (editor) {
            editor.setEditable(isEditable);
            if (isEditable) {
                editor.commands.focus();
            }
        }
    }, [isEditable, editor]);

    useEffect(() => {
        if (editor && !isEditable && mentionType === 'none') {
            editor.commands.setContent(content, false);
        }
    }, [content, editor, isEditable, mentionType]);

    const menuBar = (
        <MenuBar
            editor={editor}
            onSave={onSaveAction}
            trackMentionCharClicked={trackerFunctions?.trackMentionCharClicked}
            cancelAutoSave={cancelAutoSave}
            mentionType={mentionType}
        />
    );
    const isArgument = isArgumentFromLocation();
    return (
        <Box ref={ref} sx={{ ...sx, position: 'relative' }}>
            {isEditable && isParagraphWidget && (
                <StyPopper
                    width={parentRef?.current?.clientWidth ?? 0}
                    open={isEditable}
                    popperOptions={{
                        modifiers: [
                            {
                                name: 'preventOverflow',
                                options: {
                                    padding: 16,
                                },
                            },
                        ],
                    }}
                    anchorEl={parentRef ? parentRef.current : ref.current}
                    placement="top-start"
                >
                    {menuBar}
                </StyPopper>
            )}
            {isEditable && !isParagraphWidget && menuBar}
            <StyEditorContent isArgument={isArgument} editor={editor} />
            {isEditable && savingStatus && (
                <StySaveIndicator>
                    <CheckIcon fontSize="small" />
                    <span>{t(`textEditor.${savingStatus}`)}</span>
                </StySaveIndicator>
            )}
        </Box>
    );
}
